import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal, signal } from '@angular/core';
import { RouterOutlet, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, forkJoin, map, of, switchMap, take, tap } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { NavTileComponent } from '../components/nav-tile/nav-tile.component';
import { SectionTitleComponent } from '../components/section-title/section-title.component';
import { isPlatformAdmin } from '../core/auth/platform-admin.guard';
import { ConfigService } from '../core/config/config.service';
import { AccountService } from '../account/account.service';
import { CustomerService } from '../services/customer.service';
import { ProjectService } from '../services/project.service';
import { Membership } from '../models/membership.model';
import { DashboardPageComponent } from '../live-dashboard/dashboard-page.component';

interface HomeTile {
  link: string;
  icon: string;
  titleKey?: string;
  titleText?: string;
  subtitleKey?: string;
  subtitleText?: string;
  statLabelKey?: string;
  stat?: Signal<number | null>;
}

type HomeCase = 'single-project' | 'multi' | 'platform';

@Component({
  selector: 'app-home',
  imports: [
    RouterOutlet,
    RouterLink,
    TranslatePipe,
    MatProgressSpinner,
    MatIcon,
    NavTileComponent,
    DashboardPageComponent,
    SectionTitleComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private configService = inject(ConfigService);
  private accountService = inject(AccountService);
  private customerService = inject(CustomerService);
  private projectService = inject(ProjectService);
  private router = inject(Router);

  private readonly _loading = signal(true);
  readonly loading = this._loading.asReadonly();

  private readonly _loadError = signal(false);
  readonly loadError = this._loadError.asReadonly();

  private readonly _isPlatformAdmin = signal(false);
  readonly platformAdmin = this._isPlatformAdmin.asReadonly();

  private readonly _memberships = signal<Membership[]>([]);
  readonly memberships = this._memberships.asReadonly();

  readonly displayName = signal<string>('');
  readonly projectCount = signal<number | null>(null);
  readonly adminProjectCount = signal<number | null>(null);
  readonly customerCount = signal<number | null>(null);

  readonly homeCase = computed<HomeCase>(() => {
    if (this._isPlatformAdmin()) return 'platform';
    const memberships = this._memberships();
    if (memberships.length === 1 && memberships[0].role !== 'PROJECT_ADMIN') return 'single-project';
    return 'multi';
  });

  readonly singleProject = computed(() => {
    const memberships = this._memberships();
    return this.homeCase() === 'single-project' && memberships.length === 1 ? memberships[0] : null;
  });

  readonly tiles = computed<HomeTile[]>(() => {
    const single = this.singleProject();
    if (single) {
      return [
        {
          link: `/projects/${single.projectId}`,
          icon: 'work',
          titleText: single.projectName,
          subtitleKey: 'home.tiles.singleProject.subtitle',
        },
      ];
    }
    const tiles: HomeTile[] = [
      {
        link: '/dashboard',
        icon: 'bar_chart',
        titleKey: 'home.tiles.dashboard.title',
        subtitleKey: 'home.tiles.dashboard.subtitle',
        statLabelKey: 'home.tiles.stats.projects',
        stat: this.projectCount,
      },
    ];
    if (this._isPlatformAdmin()) {
      tiles.push({
        link: '/client-management',
        icon: 'group',
        titleKey: 'header.breadcrumb.clientManagement',
        subtitleKey: 'home.tiles.clientManagement.subtitle',
        statLabelKey: 'home.tiles.stats.customers',
        stat: this.customerCount,
      });
    }
    return tiles;
  });

  readonly tileGridClass = computed(() =>
    this.tiles().length <= 1 ? 'home__tile-grid home__tile-grid--single' : 'home__tile-grid',
  );

  ngOnInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        map(() => isPlatformAdmin(this.msalService, this.configService.azure.tenantId)),
        tap((platformAdmin) => this._isPlatformAdmin.set(platformAdmin)),
        switchMap((platformAdmin) =>
          forkJoin({
            profile: this.accountService.getProfile(),
            memberships: this.accountService.getMemberships().pipe(catchError(() => of<Membership[]>([]))),
            visibleProjectCount: this.projectService.list().pipe(
              map((projects) => projects.length),
              catchError(() => of<number | null>(null)),
            ),
            customerCount: platformAdmin
              ? this.customerService.getCustomers().pipe(
                  map((customers) => customers.length),
                  catchError(() => of<number | null>(null)),
                )
              : of<number | null>(null),
          }),
        ),
      )
      .subscribe({
        next: ({ profile, memberships, visibleProjectCount, customerCount }) => {
          this.displayName.set(profile.displayName);
          this._memberships.set(memberships);
          this.projectCount.set(visibleProjectCount ?? memberships.length);
          this.adminProjectCount.set(memberships.filter((m) => m.role === 'PROJECT_ADMIN').length);
          if (customerCount !== null) this.customerCount.set(customerCount);
          this._loading.set(false);
        },
        error: () => {
          this._loadError.set(true);
          this._loading.set(false);
        },
      });
  }

  onTileClick(tile: HomeTile): void {
    void this.router.navigateByUrl(tile.link);
  }
}
